/**
 * HostConsole — the main host screen (F1).
 *
 * Two phases:
 *  1. Configure & create a session (name, source mode auto|manual, target lang,
 *     providers, TTS toggle) → POST /sessions.
 *  2. Once a session exists: pick an audio source, Start/Stop capture (FAB),
 *     watch connection status, and read live subtitles.
 *
 * The capture pipeline (media → worklet → VAD → WS uplink) is owned by a
 * CaptureController instance held in a ref; incoming translation events are fed
 * into the subtitle store.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
} from 'react';
import { Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuthStore } from '@/features/auth/authStore';
import {
  sessionApi,
  type CreateSessionInput,
  type Session,
  type SourceLangMode,
} from '@/features/session/sessionApi';
import {
  viewerLinkApi,
  buildViewerUrl,
} from '@/features/session/viewerLinkApi';
import { DeviceSelector } from '@/features/capture/DeviceSelector';
import { OutputDeviceSelector } from '@/features/capture/outputDeviceSelector';
import { CaptureController } from '@/features/capture/captureController';
import type { CaptureSource } from '@/features/capture/audioDevices';
import { SubtitleView } from '@/features/subtitles/SubtitleView';
import { useSubtitleStore } from '@/features/subtitles/subtitleStore';
import { useTtsPlayback, type EventSubscribe } from '@/features/tts/useTtsPlayback';
import type { EventEnvelopeV1 } from '@/shared/models/events';
import type { WsStatus } from '@/shared/ws/wsClient';

const TARGET_LANGS = [
  { value: 'en-US', label: 'Inglês (EUA)' },
  { value: 'es-ES', label: 'Espanhol (Espanha)' },
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'fr-FR', label: 'Francês' },
  { value: 'de-DE', label: 'Alemão' },
];

const STATUS_LABEL: Record<WsStatus, string> = {
  idle: 'Ocioso',
  connecting: 'Conectando…',
  open: 'Conectado',
  closed: 'Desconectado',
  reconnecting: 'Reconectando…',
};

export function HostConsole(): ReactElement {
  const token = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const clearSubtitles = useSubtitleStore((s) => s.clear);
  const ingest = useSubtitleStore((s) => s.ingest);

  const controllerRef = useRef<CaptureController | null>(null);
  const sourceRef = useRef<CaptureSource>({ kind: 'mic', deviceId: null });

  // Fan-out of live WS events to interested consumers (subtitles + TTS).
  const listenersRef = useRef(new Set<(event: EventEnvelopeV1) => void>());

  const [session, setSession] = useState<Session | null>(null);
  const [creating, setCreating] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [status, setStatus] = useState<WsStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const subscribe = useCallback<EventSubscribe>((listener) => {
    listenersRef.current.add(listener);
    return () => listenersRef.current.delete(listener);
  }, []);
  const handleEvent = useCallback(
    (event: EventEnvelopeV1) => {
      ingest(event);
      listenersRef.current.forEach((fn) => fn(event));
    },
    [ingest],
  );
  const tts = useTtsPlayback({ subscribe, onError: setError });

  // Viewer share-link state (F2).
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [copied, setCopied] = useState(false);

  // Session form state.
  const [name, setName] = useState('');
  const [mode, setMode] = useState<SourceLangMode>('auto');
  const [sourceLang, setSourceLang] = useState('pt-BR');
  const [targetLang, setTargetLang] = useState('en-US');
  const [providerStt, setProviderStt] = useState('openai');
  const [providerTranslate, setProviderTranslate] = useState('openai');
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [providerTts, setProviderTts] = useState('openai');

  const handleSourceChange = useCallback((source: CaptureSource) => {
    sourceRef.current = source;
  }, []);

  // Tear the controller down if the screen unmounts mid-capture.
  useEffect(() => {
    // React cleanup runs on SPA navigation/unmount, but NOT when the tab or
    // browser is closed or reloaded. `pagehide` (more reliable than
    // `beforeunload` on mobile/bfcache) covers those cases so the host WS is
    // always closed and the session finalized server-side.
    const closeOnExit = (): void => {
      void controllerRef.current?.stop();
    };
    window.addEventListener('pagehide', closeOnExit);
    window.addEventListener('beforeunload', closeOnExit);
    return () => {
      window.removeEventListener('pagehide', closeOnExit);
      window.removeEventListener('beforeunload', closeOnExit);
      void controllerRef.current?.stop();
    };
  }, []);

  async function handleCreate(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setCreating(true);
    setError(null);
    try {
      const input: CreateSessionInput = {
        name: name.trim(),
        source_lang_mode: mode,
        source_lang: mode === 'manual' ? sourceLang : null,
        target_lang: targetLang,
        tts_enabled: ttsEnabled,
        provider_stt: providerStt,
        provider_translate: providerTranslate,
        provider_tts: ttsEnabled ? providerTts : null,
      };
      setSession(await sessionApi.create(input));
    } catch {
      setError('Não foi possível criar a sessão.');
    } finally {
      setCreating(false);
    }
  }

  async function startCapture(): Promise<void> {
    if (!session || !token) return;
    setError(null);
    clearSubtitles();
    // Starting capture is a user gesture — unlock audio so TTS chunks can play
    // (browser autoplay policy) when TTS is enabled for this session.
    tts.unlockAudio();
    const controller = new CaptureController({
      sessionId: session.id,
      token,
      onStatus: setStatus,
      onEvent: handleEvent,
      onError: setError,
    });
    controllerRef.current = controller;
    try {
      await controller.start(sourceRef.current);
      setCapturing(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao iniciar a captura.');
      controllerRef.current = null;
    }
  }

  async function stopCapture(): Promise<void> {
    await controllerRef.current?.stop();
    controllerRef.current = null;
    setCapturing(false);
    setStatus('closed');
  }

  async function endSession(): Promise<void> {
    // Stop any live capture (closes the host WS, which finalizes the session
    // server-side), then reset local state back to the create-session form.
    await controllerRef.current?.stop();
    controllerRef.current = null;
    setCapturing(false);
    setStatus('idle');
    setSession(null);
    setViewerUrl(null);
    setCopied(false);
    clearSubtitles();
    setError(null);
  }

  async function shareWithAudience(): Promise<void> {
    if (!session) return;
    setSharing(true);
    setError(null);
    try {
      const link = await viewerLinkApi.create(session.id);
      setViewerUrl(buildViewerUrl(link.token));
      setCopied(false);
    } catch {
      setError('Não foi possível gerar o link de audiência.');
    } finally {
      setSharing(false);
    }
  }

  async function copyViewerUrl(): Promise<void> {
    if (!viewerUrl) return;
    try {
      await navigator.clipboard.writeText(viewerUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Não foi possível copiar o link.');
    }
  }

  return (
    <main style={styles.page}>
      <nav style={styles.nav}>
        <strong style={styles.brand}>RealtimeTranslator</strong>
        <Link to="/history" style={styles.link}>
          Histórico
        </Link>
        <Link to="/credentials" style={styles.link}>
          Credenciais
        </Link>
        <button type="button" style={styles.logout} onClick={logout}>
          Sair
        </button>
      </nav>

      <div style={styles.grid}>
        <section style={styles.panel}>
          <h1 style={styles.title}>Console do host</h1>

          {!session ? (
            <form onSubmit={(e) => void handleCreate(e)} style={styles.form}>
              <label style={styles.field}>
                <span style={styles.label}>Nome da sessão</span>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Palestra de abertura"
                  style={styles.input}
                />
              </label>

              <label style={styles.field}>
                <span style={styles.label}>Idioma de origem</span>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value as SourceLangMode)}
                  style={styles.input}
                >
                  <option value="auto">Detecção automática</option>
                  <option value="manual">Fixo (manual)</option>
                </select>
              </label>

              {mode === 'manual' && (
                <label style={styles.field}>
                  <span style={styles.label}>Idioma de origem (fixo)</span>
                  <select
                    value={sourceLang}
                    onChange={(e) => setSourceLang(e.target.value)}
                    style={styles.input}
                  >
                    {TARGET_LANGS.map((l) => (
                      <option key={l.value} value={l.value}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              <label style={styles.field}>
                <span style={styles.label}>Idioma de destino</span>
                <select
                  value={targetLang}
                  onChange={(e) => setTargetLang(e.target.value)}
                  style={styles.input}
                >
                  {TARGET_LANGS.map((l) => (
                    <option key={l.value} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>

              <div style={styles.row}>
                <label style={styles.field}>
                  <span style={styles.label}>Provider STT</span>
                  <select
                    value={providerStt}
                    onChange={(e) => setProviderStt(e.target.value)}
                    style={styles.input}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="deepgram">Deepgram</option>
                    <option value="azure">Azure</option>
                  </select>
                </label>
                <label style={styles.field}>
                  <span style={styles.label}>Provider tradução</span>
                  <select
                    value={providerTranslate}
                    onChange={(e) => setProviderTranslate(e.target.value)}
                    style={styles.input}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="deepl">DeepL</option>
                    <option value="azure">Azure</option>
                  </select>
                </label>
              </div>

              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={ttsEnabled}
                  onChange={(e) => setTtsEnabled(e.target.checked)}
                />
                <span>Habilitar TTS (síntese de voz)</span>
              </label>

              {ttsEnabled && (
                <label style={styles.field}>
                  <span style={styles.label}>Provider TTS</span>
                  <select
                    value={providerTts}
                    onChange={(e) => setProviderTts(e.target.value)}
                    style={styles.input}
                  >
                    <option value="openai">OpenAI</option>
                    <option value="elevenlabs">ElevenLabs</option>
                  </select>
                </label>
              )}

              <button type="submit" style={styles.primaryButton} disabled={creating}>
                {creating ? 'Criando…' : 'Criar sessão'}
              </button>
            </form>
          ) : (
            <div style={styles.form}>
              <div style={styles.sessionHead}>
                <div>
                  <strong>{session.name}</strong>
                  <div style={styles.metaLine}>
                    {session.source_lang_mode === 'auto'
                      ? 'origem: auto'
                      : `origem: ${session.source_lang ?? '—'}`}{' '}
                    → {session.target_lang}
                  </div>
                </div>
                <div style={styles.headRight}>
                  <span style={{ ...styles.statusPill, ...statusColor(status) }}>
                    {STATUS_LABEL[status]}
                  </span>
                  <button
                    type="button"
                    onClick={() => void endSession()}
                    style={styles.endButton}
                  >
                    Encerrar sessão
                  </button>
                </div>
              </div>

              <DeviceSelector disabled={capturing} onChange={handleSourceChange} />

              {tts.isContingency && (
                <div style={styles.contingency} role="alert">
                  ⚠️ TTS indisponível — modo legenda apenas
                </div>
              )}

              <OutputDeviceSelector
                disabled={tts.isContingency}
                supported={tts.sinkSelectionSupported}
                onChange={tts.setSinkId}
              />

              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={tts.muted}
                  disabled={tts.isContingency}
                  onChange={(e) => tts.setMuted(e.target.checked)}
                />
                <span>Silenciar áudio da tradução (TTS)</span>
              </label>

              <button
                type="button"
                onClick={() => void (capturing ? stopCapture() : startCapture())}
                style={{
                  ...styles.fab,
                  background: capturing ? 'var(--rt-danger)' : 'var(--rt-primary)',
                }}
              >
                {capturing ? '■ Parar captura' : '● Iniciar captura'}
              </button>

              <div style={styles.shareBlock}>
                <button
                  type="button"
                  onClick={() => void shareWithAudience()}
                  disabled={sharing}
                  style={styles.shareButton}
                >
                  {sharing ? 'Gerando link…' : '🔗 Compartilhar com a audiência'}
                </button>

                {viewerUrl && (
                  <div style={styles.shareResult}>
                    <div style={styles.urlRow}>
                      <input
                        type="text"
                        readOnly
                        value={viewerUrl}
                        style={styles.urlInput}
                        onFocus={(e) => e.currentTarget.select()}
                        aria-label="Link de audiência"
                      />
                      <button
                        type="button"
                        onClick={() => void copyViewerUrl()}
                        style={styles.copyButton}
                      >
                        {copied ? 'Copiado!' : 'Copiar'}
                      </button>
                    </div>
                    <div style={styles.qrWrap}>
                      <QRCodeSVG
                        value={viewerUrl}
                        size={168}
                        includeMargin
                        level="M"
                      />
                    </div>
                    <p style={styles.shareHint}>
                      A audiência acessa este link (ou lê o QR Code) sem login.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {error && <p style={styles.error}>{error}</p>}
        </section>

        <section style={styles.subtitlePanel}>
          <h2 style={styles.subtitleTitle}>Legendas ao vivo</h2>
          <SubtitleView />
        </section>
      </div>
    </main>
  );
}

function statusColor(status: WsStatus): React.CSSProperties {
  if (status === 'open') return { color: 'var(--rt-success)' };
  if (status === 'reconnecting' || status === 'connecting')
    return { color: 'var(--rt-text-muted)' };
  return { color: 'var(--rt-text-muted)' };
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--rt-bg)', color: 'var(--rt-text)' },
  nav: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    padding: '0.75rem 1.5rem',
    borderBottom: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
  },
  brand: { fontSize: '0.95rem', marginRight: 'auto' },
  link: { color: 'var(--rt-primary)', textDecoration: 'none', fontSize: '0.9rem' },
  logout: {
    border: '1px solid var(--rt-border)',
    background: 'transparent',
    color: 'var(--rt-text)',
    borderRadius: 8,
    padding: '0.35rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(320px, 420px) 1fr',
    gap: '1.25rem',
    padding: '1.5rem',
    alignItems: 'start',
  },
  panel: {
    background: 'var(--rt-surface)',
    border: '1px solid var(--rt-border)',
    borderRadius: 12,
    padding: '1.25rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  title: { margin: 0, fontSize: '1.3rem' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: 1, minWidth: 140 },
  label: { fontSize: '0.8rem', color: 'var(--rt-text-muted)' },
  input: {
    padding: '0.6rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    fontSize: '0.9rem',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
  },
  primaryButton: {
    padding: '0.65rem 1rem',
    borderRadius: 8,
    border: 'none',
    background: 'var(--rt-primary)',
    color: 'var(--rt-primary-text)',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  sessionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.75rem',
  },
  headRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '0.4rem',
  },
  endButton: {
    border: '1px solid var(--rt-danger)',
    background: 'transparent',
    color: 'var(--rt-danger)',
    borderRadius: 8,
    padding: '0.3rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  metaLine: { fontSize: '0.8rem', color: 'var(--rt-text-muted)', marginTop: 2 },
  statusPill: {
    fontSize: '0.75rem',
    padding: '0.2rem 0.5rem',
    borderRadius: 999,
    border: '1px solid var(--rt-border)',
    whiteSpace: 'nowrap',
  },
  fab: {
    padding: '0.85rem 1rem',
    borderRadius: 999,
    border: 'none',
    color: 'var(--rt-primary-text)',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
  },
  subtitlePanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    minHeight: 320,
  },
  shareBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: '1px solid var(--rt-border)',
  },
  shareButton: {
    padding: '0.65rem 1rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-surface)',
    color: 'var(--rt-text)',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  shareResult: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  urlRow: { display: 'flex', gap: '0.5rem' },
  urlInput: {
    flex: 1,
    padding: '0.55rem 0.6rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    fontSize: '0.85rem',
  },
  copyButton: {
    padding: '0.55rem 0.9rem',
    borderRadius: 8,
    border: 'none',
    background: 'var(--rt-primary)',
    color: 'var(--rt-primary-text)',
    cursor: 'pointer',
    fontSize: '0.85rem',
    whiteSpace: 'nowrap',
  },
  qrWrap: {
    alignSelf: 'center',
    padding: '0.75rem',
    background: '#ffffff',
    borderRadius: 12,
    lineHeight: 0,
  },
  shareHint: {
    margin: 0,
    fontSize: '0.8rem',
    color: 'var(--rt-text-muted)',
    textAlign: 'center',
  },
  subtitleTitle: { margin: 0, fontSize: '1.05rem' },
  error: { color: 'var(--rt-danger)', fontSize: '0.85rem', margin: 0 },
  contingency: {
    padding: '0.6rem 0.75rem',
    borderRadius: 8,
    border: '1px solid var(--rt-danger)',
    background: 'color-mix(in srgb, var(--rt-danger) 12%, transparent)',
    color: 'var(--rt-danger)',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
};
